/**
 * Shop Collection Handler
 * Replica of TypeScript-master shop-collection.directive.ts
 * Handles variant selection, image updates, price changes, and add-to-cart for shop collection items
 */

class ShopCollectionHandler {
  constructor(element) {
    this.host = element;
    this.variants = [];
    this.data = null;
    this.hasChosenVariant = false;
    this.usingVariantMediaGallery = false;
    this.originalGalleryHTML = '';
    
    // Cache DOM elements
    this.dataEl = this.host.querySelector('[shop-collection-data]');
    this.gallery = this.host.querySelector('.shop-collection__item-images');
    this.images = this.host.querySelectorAll('[media-id]');
    this.fieldsets = this.host.querySelectorAll('fieldset');
    this.minusBtn = this.host.querySelector('.product-form__quantity-subtract-item');
    this.addBtn = this.host.querySelector('.product-form__quantity-add-item');
    this.quantityInput = this.host.querySelector('.product-form__input--quantity');
    this.addToCartBtn = this.host.querySelector('.bttn[add-to-cart]');
    this.links = this.host.querySelectorAll('a[product-link]');
    this.priceEl = this.host.querySelector('.shop-collection__item-price');
    this.form = this.host.closest('form');
    
    this.init();
  }

  init() {
    if (!this.dataEl || !this.gallery) return;

    // Store original gallery HTML for restoration
    this.originalGalleryHTML = this.gallery.innerHTML;
    
    // Parse product data
    this.parseData();
    
    // Ensure main image is marked
    this.ensureMainImage();
    
    // Sync with main variant selector if exists
    this.syncWithMainVariantSelector();
    
    // Bind events
    this.bindEvents();
  }

  parseData() {
    try {
      this.data = JSON.parse(this.dataEl.textContent);
      const { variant_metafields, variants } = this.data;
      
      // Merge variants with their metafields
      this.variants = variants.map((variant, i) => {
        return { 
          ...variant, 
          metafields: variant_metafields[i] || {} 
        };
      });
    } catch (err) {
      // Silent fail
    }
  }

  ensureMainImage() {
    const mainImg = Array.from(this.images).find(img => 
      img.classList.contains('main-img')
    );
    
    if (!mainImg && this.images.length > 0) {
      this.images[0].classList.add('main-img');
    }
  }

  syncWithMainVariantSelector() {
    // Check if there's a main variant selector on the page (for PDP)
    const variantSelect = document.querySelector('[variant-select]');
    
    if (variantSelect && variantSelect.variantSelectHandler) {
      const handler = variantSelect.variantSelectHandler;
      
      // Subscribe to variant changes
      if (handler.subscribeToVariantChanges) {
        handler.subscribeToVariantChanges(() => {
          this.syncWithVariantSelect(handler);
        });
      }
      
      // Initial sync
      setTimeout(() => {
        this.syncWithVariantSelect(handler);
      }, 100);
    }
  }

  syncWithVariantSelect(variantSelectHandler) {
    if (!this.data?.variants) return;
    
    const mainVariant = variantSelectHandler.currentVariant;
    if (!mainVariant) return;
    
    const optionInputs = Array.from(
      this.host.querySelectorAll('input[type="radio"]')
    );
    
    // Match TypeScript-master exactly: only change if input is NOT checked
    // If already checked, do nothing (no event, no update)
    for (const option of mainVariant.options) {
      const matchingInput = optionInputs.find(input => input.value == option);
      
      if (matchingInput && !matchingInput.checked) {
        matchingInput.checked = true;
        matchingInput.setAttribute('checked', '');
        this.hasChosenVariant = false;
        matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  bindEvents() {
    // Listen to option changes within this specific item (not form-wide)
    this.host.addEventListener('change', this.onFieldChange.bind(this));
    
    // Quantity controls
    if (this.addBtn) {
      this.addBtn.addEventListener('click', this.onAddBtnClick.bind(this));
    }
    
    if (this.minusBtn) {
      this.minusBtn.addEventListener('click', this.onMinusBtnClick.bind(this));
    }
  }

  onFieldChange(event) {
    const target = event.target;
    
    // Only handle radio inputs
    if (target.type !== 'radio') return;
    
    const fieldsetIdx = Array.from(this.fieldsets).findIndex(fieldset => 
      fieldset.contains(target)
    );
    
    if (fieldsetIdx === -1) return;
    
    // Build options array
    const opts = Array.from(this.fieldsets).map(() => undefined);
    opts[fieldsetIdx] = target.value;
    
    // Fill in previous fieldset values
    for (let i = 0; i < fieldsetIdx; i++) {
      const input = this.fieldsets[i].querySelector('input:checked');
      if (input) {
        opts[i] = input.value;
      }
    }
    
    // Determine final options based on fieldset position
    let finalOpts;
    if (fieldsetIdx === this.fieldsets.length - 1) {
      finalOpts = opts.slice(0, fieldsetIdx).filter(opt => !!opt);
    } else {
      finalOpts = opts.filter(opt => !!opt);
    }
    
    // Find potential variants matching current selection
    const potentialVariants = this.variants?.filter(variant => 
      finalOpts.every((opt, i) => variant.options[i] === opt)
    );
    
    // Recalculate availability for select-product-options
    this.recalculateSelectOptions(potentialVariants);
    
    // Update UI
    this.updateFieldsetValue(target.value);
    this.updateLinks();
    this.updateImages();
    this.updatePrice();
    this.updateAddToCart();
  }

  recalculateSelectOptions(potentialVariants) {
    // Find and update select-product-option directives
    const selectOptions = this.host.querySelectorAll('[select-product-option]');
    
    selectOptions.forEach(selectOption => {
      // Initialize handler if not already done
      if (!selectOption.selectProductOptionHandler && window.SelectProductOptionHandler) {
        selectOption.selectProductOptionHandler = new window.SelectProductOptionHandler(selectOption);
      }
      
      if (selectOption.selectProductOptionHandler) {
        selectOption.selectProductOptionHandler.recalculate(potentialVariants);
        selectOption.selectProductOptionHandler.showCurrentValue();
      }
    });
  }

  get currentOptions() {
    return Array.from(this.fieldsets).map(fieldset => {
      const input = fieldset.querySelector('input:checked');
      return input ? input.value : null;
    });
  }

  get currentVariant() {
    if (!this.variants || this.variants.length === 0) return null;
    
    const options = this.currentOptions;
    
    return this.variants.find(variant => 
      variant.options.every((option, i) => options[i] === option)
    );
  }

  updateImages() {
    if (!this.currentVariant) return;

    const selectedVariantMediaGallery = 
      this.currentVariant.metafields?.variant_media_gallery;

    // Priority 1: Use variant_media_gallery if available
    if (selectedVariantMediaGallery && selectedVariantMediaGallery.length > 0) {
      this.usingVariantMediaGallery = true;
      this.replaceGalleryContent(selectedVariantMediaGallery);
    } 
    // Priority 2: Use featured_media
    else if (this.currentVariant.featured_media) {
      // Restore original gallery if we were using variant media gallery
      if (this.usingVariantMediaGallery) {
        this.restoreOriginalGallery();
        this.usingVariantMediaGallery = false;
      }

      this.updateFeaturedImage(this.currentVariant.featured_media.id);
    }

    // Duplicate check (matches TypeScript-master line 207-211)
    if (selectedVariantMediaGallery?.length > 0) {
      this.replaceGalleryContent(selectedVariantMediaGallery);
    } else {
      this.updateImage();
    }
  }

  restoreOriginalGallery() {
    if (this.originalGalleryHTML && this.usingVariantMediaGallery) {
      this.gallery.innerHTML = this.originalGalleryHTML;
      this.images = this.host.querySelectorAll('[media-id]');
    }
  }

  updateFeaturedImage(mediaId) {
    const targetImage = Array.from(this.images).find(img => 
      Number(img.getAttribute('media-id')) === mediaId
    );

    // Remove main-img from all images
    this.images.forEach(img => img.classList.remove('main-img'));

    if (targetImage) {
      targetImage.classList.add('main-img');
    } else {
      this.images[0]?.classList.add('main-img');
    }
  }

  updateImage() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    const newImg = Array.from(this.images).find((img) => 
      Number(img.getAttribute('media-id')) === this.currentVariant?.featured_media.id
    );

    if (newImg) {
      Array.from(this.images).forEach(img => img.classList.remove('main-img'));
      newImg.classList.add('main-img');
    }
  }

  replaceGalleryContent(mediaList) {
    if (!mediaList || mediaList.length === 0) {
      this.updateImage();
      return;
    }

    let html = '';
    const widths = [540, 720, 900, 1080, 1250, 1440, 1680, 1920];

    mediaList.forEach((media, idx) => {
      // Clean base URL
      let baseUrl = media.url || media.preview_image;
      baseUrl = baseUrl.replace(/[?&]width=\d+/g, '').replace(/[?&]height=\d+/g, '');
      const separator = baseUrl.includes('?') ? '&' : '?';
      
      // Generate srcset
      const srcsetParts = widths.map(w => {
        return `${baseUrl}${separator}crop=center&width=${w}&height=${w} ${w}w`;
      });
      const srcset = srcsetParts.join(', ');
      
      // Placeholder src
      const placeholderSrc = `${baseUrl}${separator}crop=center&width=16&height=16`;
      
      html += `
        <div media-id="${media.id}" class="shop-collection__item-imageHolder ${idx === 0 ? 'main-img' : ''}">
          <img
            src="${placeholderSrc}"
            srcset="${srcset}"
            sizes="(min-width: 1440px) 90vw, (min-width: 768px) 80vw, 100vw"
            class="inlineImg inlineImg--responsive"
            loading="lazy"
            decoding="async"
            alt="${media.alt || ''}"
          />
        </div>
      `;
    });
    
    this.gallery.innerHTML = html;
    this.images = this.host.querySelectorAll('[media-id]');
  }

  updateFieldsetValue(value) {
    const optionLabels = this.host.querySelectorAll('[variant-selected-option]');
    
    optionLabels.forEach(label => {
      label.textContent = value;
    });
  }

  updateLinks(variant = this.currentVariant) {
    if (!variant) return;
    
    this.links.forEach(link => {
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', variant.id);
        link.href = `${url.pathname}?${url.searchParams.toString()}`;
      } catch (err) {
        // Silent fail
      }
    });
  }

  updatePrice() {
    const variant = this.currentVariant;
    
    if (variant && this.priceEl) {
      this.priceEl.textContent = this.formatMoney(variant.price);
    }
  }

  updateAddToCart(disabledLabel = 'Unavailable') {
    if (!this.addToCartBtn) return;
    
    const variant = this.currentVariant;
    
    if (variant && variant.available) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.setAttribute('add-to-cart', variant.id);
      this.addToCartBtn.textContent = 'Add to cart';
      this.hasChosenVariant = true;
    } else {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = disabledLabel;
      this.hasChosenVariant = false;
    }
  }

  onAddBtnClick(event) {
    event.preventDefault();
    event.stopPropagation(); // Prevent event bubbling
    
    if (!this.quantityInput) return;
    
    // Parse the value properly (handle leading zeros like "01")
    const currentQty = parseInt(this.quantityInput.value, 10) || 1;
    this.quantityInput.value = (currentQty + 1).toString();
    
    // Enable minus button
    if (this.minusBtn) {
      this.minusBtn.disabled = false;
    }
  }

  onMinusBtnClick(event) {
    event.preventDefault();
    event.stopPropagation(); // Prevent event bubbling
    
    if (!this.quantityInput) return;
    
    const currentQty = parseInt(this.quantityInput.value) || 1;
    
    if (currentQty <= 1) {
      this.minusBtn.disabled = true;
      return;
    }
    
    this.quantityInput.value = currentQty - 1;
    
    if (currentQty - 1 === 1) {
      this.minusBtn.disabled = true;
    }
  }

  // Shopify money formatting
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

/**
 * Initialize all shop collection items on the page
 */
function initializeShopCollectionHandlers() {
  const shopCollectionItems = document.querySelectorAll('.shop-collection__item');
  
  shopCollectionItems.forEach(item => {
    // Don't re-initialize
    if (item.shopCollectionHandler) return;
    
    const handler = new ShopCollectionHandler(item);
    item.shopCollectionHandler = handler;
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeShopCollectionHandlers);
} else {
  initializeShopCollectionHandlers();
}

// Re-initialize on section load (Shopify Sections API)
document.addEventListener('shopify:section:load', initializeShopCollectionHandlers);

// Re-initialize on AJAX loads
document.addEventListener('ajax:complete', initializeShopCollectionHandlers);

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ShopCollectionHandler;
}

// Export to window for cross-script access
if (typeof window !== 'undefined') {
  window.ShopCollectionHandler = ShopCollectionHandler;
}

